import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { getSourceAndLink } from "pages/FindSection/functions"
import { DataList } from "../types"

type ExportExcel = {
  data: DataList[]
  dateFilter: string
  companyName?: string
  module?: string
}

export const exportExcelFindInventory = async ({
  data,
  dateFilter,
  module
}: ExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "DATE",
    "ID#",
    "SOURCE",
    "ACCOUNT",
    "ACCOUNT NAME",
    "MEMO",
    "QTY",
    "UNIT",
    "DEBIT",
    "CREDIT",
    "JOB ID",
    "LOCATION"
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter, module)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `find-records-inventory-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

function makeTitle(worksheet: Worksheet, dateFilter: string, module?: string) {
  const endColumnIdx = 13

  worksheet.mergeCells(1, 1, 1, endColumnIdx)
  const inventoryCell = worksheet.getCell(1, 1)
  inventoryCell.value = module
  inventoryCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }
  inventoryCell.font = { bold: true, size: 15 }

  worksheet.mergeCells(2, 1, 2, endColumnIdx)
  const titleCell = worksheet.getCell(2, 1)
  titleCell.value = `FIND RECORDS ${module ?? ""}`
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }
  titleCell.font = { bold: true, size: 15 }

  const dateRowIdx = 3
  worksheet.mergeCells(dateRowIdx, 1, dateRowIdx, endColumnIdx)
  const dateFilterCell = worksheet.getCell(dateRowIdx, 1)
  dateFilterCell.value = `DATE: ${dateFilter}`
  dateFilterCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }
  dateFilterCell.font = { bold: true, size: 13 }

  for (let i = 1; i <= endColumnIdx; i++) {
    worksheet.getColumn(i).width = 20
  }
}


function makeRows(data: ExportExcel["data"], worksheet: Worksheet) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.trx_code,
      item.data_source ? getSourceAndLink(item.data_source, item)?.source : "-",
      `${item?.account.group_coa_code} - ${item?.account.coa_code}`,
      item.account.coa_name,
      item.memo,
      item.qty ?? 0,
      item.unit?.mr_unit_name ?? "",
      item.position === 1 && item.amount > 0 ? item.amount : "",
      item.position === 2 && item.amount > 0 ? item.amount : "",
      item.project.project_name,
      item.location.location_name
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = data.reduce(
    (acc, item) => {
      return {
        debit: acc.debit + (item.position === 1 ? item.amount : 0),
        credit: acc.credit + (item.position === 2 ? item.amount : 0)
      }
    },
    { debit: 0, credit: 0 }
  )

  // summary
  const calculateDebitCredit = worksheet.addRow(
    Array(9).fill("").concat([calculate.debit, calculate.credit, "#", "#"])
  )

  calculateDebitCredit.getCell(10).font = { bold: true }
  calculateDebitCredit.getCell(11).font = { bold: true }
  calculateDebitCredit.eachCell((cell) => {
    cell.border = cellBorder
  })
}
